/**
 * פניות ומשוב — what staff report about the system, and how the team answers (2026-10-21).
 *
 * Built from an approved Claude Design mockup. Codes are English, labels are Hebrew (ADR-017).
 * Two sides of one screen: every staff member files and follows their own requests (`feedback:submit`,
 * held by anyone who is not a portal-only customer — `pagePermissions.ts`), and whoever holds the
 * "ניהול פניות ומשוב" page triages all of them (`feedback:manage`).
 *
 * No React, no MUI, no browser globals — the backend imports this file through
 * `@malash-office/types/feedback`.
 */

/* ── kind ─────────────────────────────────────────────────────────────────────────────────── */

export const FEEDBACK_TYPES = ['bug', 'feature', 'improvement', 'idea', 'question'] as const;
export type FeedbackType = (typeof FEEDBACK_TYPES)[number];

export const FEEDBACK_TYPE_LABELS: Record<FeedbackType, string> = {
  bug: 'באג',
  feature: "בקשה לפיצ'ר חדש",
  improvement: "שיפור לפיצ'ר קיים",
  idea: 'רעיון / עצה',
  question: 'שאלה / עזרה'
};

/** The line under each quick-pick tile — "מה תרצה לשתף?". */
export const FEEDBACK_TYPE_HINTS: Record<FeedbackType, string> = {
  bug: 'משהו לא עובד כמו שצריך',
  feature: 'יכולת שחסרה לך במערכת',
  improvement: 'שינוי במשהו שכבר קיים',
  idea: 'הצעה לשיפור דרך העבודה',
  question: 'לא בטוח איך עושים משהו'
};

/* ── status ───────────────────────────────────────────────────────────────────────────────── */

export const FEEDBACK_STATUSES = ['new', 'reviewing', 'in_progress', 'planned', 'done', 'rejected'] as const;
export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number];

export const FEEDBACK_STATUS_LABELS: Record<FeedbackStatus, string> = {
  new: 'חדש',
  reviewing: 'בבדיקה',
  in_progress: 'בטיפול',
  planned: 'מתוכנן',
  done: 'הושלם',
  rejected: 'נדחה'
};

/** Still somebody's work — "פתוחות" in "X פתוחות מתוך Y", and the KPI for open high-urgency bugs. */
export const FEEDBACK_OPEN_STATUSES: readonly FeedbackStatus[] = ['new', 'reviewing', 'in_progress'];

/** Closed for good — a team reply on one of these is not flagged "חדש". */
export const FEEDBACK_CLOSED_STATUSES: readonly FeedbackStatus[] = ['done', 'rejected'];

/* ── urgency ──────────────────────────────────────────────────────────────────────────────── */

export const FEEDBACK_URGENCIES = ['low', 'medium', 'high'] as const;
export type FeedbackUrgency = (typeof FEEDBACK_URGENCIES)[number];

export const FEEDBACK_URGENCY_LABELS: Record<FeedbackUrgency, string> = {
  low: 'נמוכה',
  medium: 'בינונית',
  high: 'גבוהה'
};

/* ── module ───────────────────────────────────────────────────────────────────────────────── */

/**
 * The screen the request is about, as the menu names it ("שילוח ספקים"). Free text on purpose:
 * the menu is the list, it grows with every new screen, and a stored label stays readable after a
 * screen is renamed. `FEEDBACK_GENERAL_MODULE` is anything not inside one screen.
 */
export const FEEDBACK_GENERAL_MODULE = 'כללי';
export const FEEDBACK_MODULE_MAX_LENGTH = 80;

/** How the team signs its replies to the person who filed the request. */
export const FEEDBACK_TEAM_NAME = 'צוות המערכת';

/** Files and screenshots — images, PDF and short video, each up to 20 MB (the mockup's rule). */
export const FEEDBACK_FILE_MAX_BYTES = 20 * 1024 * 1024;
export const FEEDBACK_FILE_MAX_COUNT = 10;
export const FEEDBACK_FILE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/pdf',
  'video/mp4',
  'video/webm',
  'video/quicktime'
] as const;

/* ── wire shapes ──────────────────────────────────────────────────────────────────────────── */

export interface FeedbackAuthorRef {
  id: number;
  name: string;
  /** The roles' names, joined — the grey line under the name ("מנהלת רכש"). */
  role: string;
}

/** One row of "הפניות שלי" and of "ניהול פניות". */
export interface FeedbackRowDto {
  id: number;
  /** `FB-1048`. */
  code: string;
  createdAt: string;
  updatedAt: string;
  author: FeedbackAuthorRef;
  type: FeedbackType;
  urgency: FeedbackUrgency;
  status: FeedbackStatus;
  module: string;
  title: string;
  fileCount: number;
  /** Messages on the thread the author sees — internal notes are not counted. */
  replyCount: number;
  /**
   * The team answered last, the author has not opened it since, and the request is still open —
   * the "חדש" badge on "הפניות שלי". Always false on the manager's list.
   */
  hasNewReply: boolean;
  /** Merged into another request: that one's code. The row is dimmed and its KPIs skip it. */
  duplicateOfCode: string | null;
}

export interface FeedbackFileDto {
  id: number;
  fileName: string;
  /** `PNG`, `PDF` — the badge on the tile. */
  extension: string;
  mimeType: string | null;
  fileSize: number | null;
  url: string;
  isScreenshot: boolean;
}

export type FeedbackMessageKind = 'reply' | 'note';

export interface FeedbackMessageDto {
  id: number;
  kind: FeedbackMessageKind;
  /** A team reply (or any note) sits on the team's side of the thread. */
  fromTeam: boolean;
  /** `FEEDBACK_TEAM_NAME` on a team reply; the staff member's own name on a note or an author reply. */
  authorName: string;
  body: string;
  createdAt: string;
}

/** The drawer. `notes` is empty for anyone who is not a manager — the server never sends them. */
export interface FeedbackDto extends FeedbackRowDto {
  description: string;
  link: string | null;
  files: FeedbackFileDto[];
  replies: FeedbackMessageDto[];
  notes: FeedbackMessageDto[];
  duplicateOfId: number | null;
}

export interface FeedbackListResponse {
  rows: FeedbackRowDto[];
}

/** The four tiles on top of "ניהול פניות". Merged duplicates are not counted. */
export interface FeedbackSummaryDto {
  newCount: number;
  activeCount: number;
  urgentBugCount: number;
  plannedCount: number;
  totalCount: number;
  /** Every module named on a request — the manager's filter offers these beside the menu's. */
  modules: string[];
}

/** The multipart fields beside the files of "פנייה חדשה". */
export interface CreateFeedbackBody {
  type: FeedbackType;
  urgency: FeedbackUrgency;
  title: string;
  description: string;
  link?: string | null;
  module: string;
}

export interface UpdateFeedbackBody {
  status?: FeedbackStatus;
  urgency?: FeedbackUrgency;
  module?: string;
}

export interface CreateFeedbackMessageBody {
  kind: FeedbackMessageKind;
  body: string;
}

export interface MergeFeedbackBody {
  /** The original request this one duplicates. */
  intoId: number;
}

export interface FeedbackListQuery {
  searchTerm?: string;
  /** Comma-separated codes of each. */
  type?: string;
  status?: string;
  urgency?: string;
  module?: string;
}

export const FEEDBACK_ERROR_CODES = {
  NOT_FOUND: 'FB_NOT_FOUND',
  NO_ACCESS: 'FB_NO_ACCESS',
  MERGE_INVALID: 'FB_MERGE_INVALID',
  FILE_REJECTED: 'FB_FILE_REJECTED'
} as const;
